import React from 'react';

import { Text } from '@fly/core';
import { FlyTableProps, SortDirection } from '@fly/shared';
import { FlySvgSprite } from '@fly/svg';
import { Skeleton, Table } from '@radix-ui/themes';
import cn from 'classnames';

import { FlyEmptyState } from '../FlyEmptyState/FlyEmptyState';
import styles from './FlyTable.module.less';
import { useTable } from './useTable';

interface FlyTableWithSkeletonProps<
  T extends Record<string, any>,
> extends FlyTableProps<T> {
  ref?: React.Ref<HTMLDivElement>;
  loading?: boolean;
  skeletonRows?: number;
  emptyStateEnrich?: React.ReactNode;
  maxHeight?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  layout?: 'fixed' | 'auto';
}

// TODO: Base this on the Table component from @fly/core, when designs are finalized
export function FlyTable<T extends Record<string, any>>({
  data = [],
  columns,
  loading = false,
  skeletonRows = 5,
  emptyStateEnrich = null,
  variant = 'ghost',
  maxHeight,
  emptyStateTitle = 'No data found',
  emptyStateDescription = '',
  ref,
  layout = 'auto',
  ...rest
}: FlyTableWithSkeletonProps<T>) {
  const { sortedData, onSort, sortColumn, sortDirection } = useTable({
    columns,
    data,
  });
  if (loading) {
    return (
      <Table.Root
        variant={variant}
        className={cn(styles.flyTableRoot, 'min-h-0 bg-transparent')}
        ref={ref}
        layout={layout}
        {...rest}
      >
        <Table.Header>
          <Table.Row>
            {columns.map((col, index) => (
              <Table.ColumnHeaderCell
                key={index}
                maxWidth={col.maxWidth ? `${col.maxWidth}px` : undefined}
                className={cn({ 'cursor-pointer select-none': col.sortable })}
              >
                <Text size='2'>{col.header}</Text>
              </Table.ColumnHeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body className='bg-transparent'>
          {Array.from({ length: skeletonRows }, (_, index) => (
            <Table.Row key={index}>
              {columns.map((col, index) => (
                <Table.Cell
                  key={index}
                  maxWidth={col.maxWidth ? `${col.maxWidth}px` : undefined}
                >
                  <Skeleton loading width='100%' height='100%' />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    );
  }

  return (
    <Table.Root
      variant={variant}
      className={cn(styles.flyTableRoot, 'min-h-0 bg-transparent')}
      style={{ '--fly-table-height': maxHeight } as any}
      ref={ref}
      layout={layout}
      {...rest}
    >
      <Table.Header>
        <Table.Row>
          {columns.map((col, index) => (
            <Table.ColumnHeaderCell
              key={index}
              maxWidth={col.maxWidth ? `${col.maxWidth}px` : undefined}
              className={cn({ 'cursor-pointer select-none': col.sortable })}
              onClick={
                col.sortable ? () => onSort(col.field as string) : undefined
              }
            >
              <div className='flex h-full w-full items-center gap-fly-2'>
                <Text size='2' className='w-full'>
                  {col.header}
                </Text>
                {col.sortable && (
                  <FlySvgSprite
                    name='accordion_chevron'
                    className={cn(styles.flySortIcon, {
                      [styles.asc]:
                        sortDirection === SortDirection.ASC &&
                        sortColumn === col.field,
                      [styles.desc]:
                        sortDirection === SortDirection.DESC &&
                        sortColumn === col.field,
                    })}
                    size={16}
                  />
                )}
              </div>
            </Table.ColumnHeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body className='bg-transparent'>
        {sortedData.length > 0 ? (
          sortedData.map((row, index) => (
            <Table.Row key={index}>
              {columns.map(({ render, sortable, sort, ...col }, colIndex) => {
                const value = (row as any)[col.field];
                return (
                  <Table.Cell
                    key={colIndex}
                    {...col}
                    maxWidth={col.maxWidth ? `${col.maxWidth}px` : undefined}
                  >
                    {render ? render(value, row, index) : String(value)}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))
        ) : (
          <Table.Row>
            <Table.Cell
              colSpan={columns.length}
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              <FlyEmptyState
                title={emptyStateTitle}
                description={emptyStateDescription}
              >
                {emptyStateEnrich}
              </FlyEmptyState>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
}
